import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getReportDef, reviewDecisionSchema } from '@app/shared';
import type { ReportRow, ReviewDecisionInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { ConfirmDialog, FormDialog } from '../../components/dialogs';
import { TextInput, applyServerErrors } from '../../components/form';
import { api } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { ReportScreen } from '../reports/ReportPage';

const TABS = [
  ['fraud-review-pending', 'Pending Orders'],
  ['fraud-review-lookup', 'Order Lookup'],
] as const;
/** The three lists of Pending Orders (release switch `fraud-review-tabs`). */
const PENDING_TABS = [
  ['fraud-review-live', 'Live Orders'],
  ['fraud-review-website', 'Website'],
  ['fraud-review-telesales', 'Telesales Orders'],
] as const;

const text = (row: ReportRow, key: string): string => String(row[key] ?? '');

function useDecision(order: ReportRow | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviewDecisionInput) =>
      api.post(`/enrollments/${text(order!, 'id')}/review`, input),
    // Both tabs show the same orders.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
  });
}

function RejectDialog({ order, onClose }: { order: ReportRow | null; onClose: () => void }) {
  const notify = useNotify();
  const decide = useDecision(order);
  const { control, handleSubmit, reset, setError } = useForm<ReviewDecisionInput>({
    defaultValues: { decision: 'Rejected', reason: '' },
    resolver: zodResolver(reviewDecisionSchema),
  });
  useEffect(() => {
    if (order) reset({ decision: 'Rejected', reason: '' });
  }, [order, reset]);
  const submit = handleSubmit(async (values) => {
    try {
      await decide.mutateAsync(values);
      notify.success('Order rejected.');
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <FormDialog
      open={Boolean(order)}
      title="Reject Order"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={decide.isPending}
      submitLabel="Reject"
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        {order ? <Typography>Enrollment {text(order, 'enrollmentId')}</Typography> : null}
        <TextInput
          control={control}
          name="reason"
          label="Reason"
          required
          autoFocus
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: 250 } }}
        />
      </Box>
    </FormDialog>
  );
}

/**
 * Review > Fraud Orders Review Queue (PC984): orders held by fraud prevention. "Pending
 * Orders" lists them by date, status, state and source; "Order Lookup" finds one order.
 */
export function ReviewQueuePage() {
  const notify = useNotify();
  const [tab, setTab] = useState<(typeof TABS)[number][0]>('fraud-review-pending');
  const [pendingTab, setPendingTab] =
    useState<(typeof PENDING_TABS)[number][0]>('fraud-review-live');
  const featureOn = useFeatureReleased();
  const split = tab === 'fraud-review-pending' && featureOn('fraud-review-tabs');
  const screen = split ? pendingTab : tab;
  const [approving, setApproving] = useState<ReportRow | null>(null);
  const [rejecting, setRejecting] = useState<ReportRow | null>(null);
  const approve = useDecision(approving);

  return (
    <>
      <ReportScreen
        // `key` gives each tab its own filters and paging.
        key={screen}
        def={getReportDef(screen)!}
        top={
          <>
            <Tabs
              value={tab}
              onChange={(_e, value: (typeof TABS)[number][0]) => setTab(value)}
              aria-label="Review queue views"
              sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
            >
              {TABS.map(([key, label]) => (
                <Tab key={key} value={key} label={label} />
              ))}
            </Tabs>
            {split ? (
              <Tabs
                value={pendingTab}
                onChange={(_e, value: (typeof PENDING_TABS)[number][0]) => setPendingTab(value)}
                aria-label="Pending orders lists"
                sx={{ mb: 1.5 }}
              >
                {PENDING_TABS.map(([key, label]) => (
                  <Tab key={key} value={key} label={label} />
                ))}
              </Tabs>
            ) : null}
          </>
        }
        rowActions={(row) =>
          text(row, 'orderStatus') === 'Pending Audit' ? (
            <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
              <Button
                size="small"
                aria-label={`Approve ${text(row, 'enrollmentId')}`}
                onClick={() => setApproving(row)}
              >
                Approve
              </Button>
              <Button
                size="small"
                color="error"
                aria-label={`Reject ${text(row, 'enrollmentId')}`}
                onClick={() => setRejecting(row)}
              >
                Reject
              </Button>
            </Box>
          ) : null
        }
      />
      <ConfirmDialog
        open={Boolean(approving)}
        title="Approve Order"
        message={
          approving
            ? `Approve enrollment ${text(approving, 'enrollmentId')}? It can then be paid and completed.`
            : ''
        }
        confirmLabel="Approve"
        busy={approve.isPending}
        onClose={() => setApproving(null)}
        onConfirm={() =>
          void approve
            .mutateAsync({ decision: 'Approved' })
            .then(() => {
              notify.success('Order approved.');
              setApproving(null);
            })
            .catch((err: unknown) => notify.error(err))
        }
      />
      <RejectDialog order={rejecting} onClose={() => setRejecting(null)} />
    </>
  );
}
